import React from 'react'
import {Link as RouteLink} from 'react-router-dom'
import PropTypes from 'prop-types'
import {Box, Link, useBreakpointValue} from '@chakra-ui/react'
import DynamicImage from '@app/components/dynamic-image'
import usePDCResponsiveElement from '../../../hooks/use-pdc-responsive-element.js'
import {DYNAMIC_IMAGE_WIDTHS} from '../../../constants.js'
import {useUrlFromLink} from '../../helpers/urlHelper'

/**
 * PDCEditoPushContent is a React component that dynamically renders an image
 * with responsive support and optional linking capabilities. It adapts the image
 * source based on the current viewport size and allows for both internal and
 * external linking.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.link - The default URL to navigate to when the image is clicked.
 * @param {Object} props.smallImage - The image object for small breakpoints.
 * @param {Object} props.mediumImage - The image object for medium breakpoints.
 * @param {Object} props.largeImage - The image object for large breakpoints.
 * @param {Object} props.xlargeImage - The image object for extra-large breakpoints.
 * @param {Object} props.xxlargeImage - The image object for double extra-large breakpoints.
 * @param {string} props.imageTitle - The title attribute for the image, used for accessibility.
 * @param {string} props.imageAlt - The alt attribute for the image, used for accessibility.
 * @param {Object} props.custom - Custom properties that may include a link object.
 * @param {Object} props.custom.link - A custom link object that can override the default link.
 * @param {string} props.custom.link.type - The type of the link, which can be 'EXTERNAL' for external links.
 * @param {boolean} displayPriority - Should image be loaded with high priority.
 * @param {string|number[]} imageSizes - The sizes attribute for the image, controlling responsive image behavior.
 *
 * @returns {JSX.Element} A JSX element that contains a responsive image wrapped in a link or a box.
 */
export const PDCEditoPushContent = ({
    link,
    smallImage,
    mediumImage,
    largeImage,
    xlargeImage,
    xxlargeImage,
    imageTitle,
    imageAlt,
    custom,
    imageSizes,
    layoutIndex
}) => {
    // Get current image based on breakpoint
    const currentImage = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: smallImage,
            mediumEl: mediumImage,
            largeEl: largeImage,
            xlargeEl: xlargeImage,
            xxlargeEl: xxlargeImage
        })
    )

    const updatedUrl = useUrlFromLink(custom?.link) || link
    const isLinkExternal = custom?.link?.type === 'EXTERNAL'

    // Choose between Link and Box
    const Container = updatedUrl ? Link : Box
    const containerProps = updatedUrl
        ? {
              as: isLinkExternal ? Link : RouteLink,
              href: updatedUrl,
              to: updatedUrl,
              textDecoration: 'none'
          }
        : {}

    return (
        <Container
            display="flex"
            justifyContent={'center'}
            alignItems={'flex-start'}
            textAlign={'center'}
            aria-label={imageAlt || imageTitle}
            {...containerProps}
        >
            {currentImage && currentImage?.url && (
                <DynamicImage
                    src={`${currentImage?.url}[?sw={width}]`}
                    widths={imageSizes === '100%' ? DYNAMIC_IMAGE_WIDTHS.full : imageSizes}
                    flex={['1 0 100%', null, 'auto']}
                    imageProps={{
                        width: '100%',
                        loading: layoutIndex < 1 ? 'eager' : 'lazy',
                        fetchpriority: layoutIndex < 1 ? 'high' : null,
                        alt: imageAlt === 'empty' ? '' : imageAlt,
                        title: imageTitle === 'empty' ? '' : imageTitle
                    }}
                />
            )}
        </Container>
    )
}

PDCEditoPushContent.propTypes = {
    link: PropTypes.string,
    smallImage: PropTypes.object.isRequired,
    mediumImage: PropTypes.object,
    largeImage: PropTypes.object,
    xlargeImage: PropTypes.object,
    xxlargeImage: PropTypes.object,
    imageTitle: PropTypes.string,
    imageAlt: PropTypes.string,
    custom: PropTypes.shape({
        link: PropTypes.shape({
            target: PropTypes.string,
            type: PropTypes.string
        })
    }),
    displayPriority: PropTypes.bool,
    imageSizes: PropTypes.any,
    layoutIndex: PropTypes.number
}
